import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { refreshUserSession } from './userSession';
import { saveSignupToken } from './naverSignupApi';
import NoticeModal from './NoticeModal';
import './AuthPage.css';

const loginErrors = {
    state: '로그인 요청이 만료되었거나 확인되지 않습니다. 로그인 버튼을 눌러 다시 시작해주세요.',
    denied: '네이버 로그인이 취소되었습니다. 다시 로그인해주세요.',
    email: '네이버 계정의 이메일 제공 동의가 필요합니다. 다시 로그인해주세요.',
    profile: '네이버 회원 정보를 확인하지 못했습니다. 정보 제공 동의를 확인해주세요.',
    disabled: '이용이 제한된 계정입니다. 관리자에게 문의해주세요.',
    linked: '이미 다른 네이버 계정이 연결되어 있습니다. 기존 계정으로 로그인해주세요.',
};

export default function NaverLoginCallback({ children }) {
    const location = useLocation();
    const navigate = useNavigate();
    const [error, setError] = useState('');
    const [signupNotice, setSignupNotice] = useState(false);
    const params = new URLSearchParams(location.search);
    const callbackStatus = params.get('naver');
    const isCallback = callbackStatus === 'ok';
    const signupToken = callbackStatus === 'signup' ? params.get('token') || '' : '';
    const reasonMessage = loginErrors[params.get('reason')];
    const callbackError = callbackStatus === 'error' || (callbackStatus === 'signup' && !signupToken)
        ? (typeof reasonMessage === 'string' ? reasonMessage : '네이버 로그인을 완료하지 못했습니다. 다시 로그인해주세요.')
        : '';

    useEffect(() => {
        // 신규 사용자는 임시 토큰을 저장하고 로그인 화면으로 옮긴 뒤 안내 팝업을 띄운다.
        if (!signupToken) return;
        try {
            saveSignupToken(signupToken);
        } catch {
            return;
        }
        sessionStorage.removeItem('garam-naver-oauth-state');
        setSignupNotice(true);
        navigate('/login', { replace: true });
    }, [signupToken, navigate]);

    useEffect(() => {
        if (!isCallback) return undefined;
        let active = true;
        setError('');
        refreshUserSession().then(() => {
            if (!active) return;
            sessionStorage.removeItem('garam-naver-oauth-state');
            const params = new URLSearchParams(location.search);
            params.delete('naver');
            params.delete('reason');
            const search = params.toString();
            navigate({ pathname: location.pathname, search: search ? `?${search}` : '', hash: location.hash }, { replace: true });
        }).catch(() => {
            if (active) setError('네이버 로그인을 완료하지 못했습니다. 다시 로그인해주세요.');
        });
        return () => { active = false; };
    }, [isCallback, location.pathname, location.search, location.hash, navigate]);

    if (signupToken || signupNotice) return <>
        {children}
        {signupNotice && <NoticeModal title="가입된 회원 정보가 없어요" message="회원가입 후 이용해주세요." onConfirm={() => { setSignupNotice(false); navigate('/signup/naver'); }} />}
    </>;
    if (!isCallback && !callbackError) return children;
    const message = callbackError || error;
    return <div className="auth-page"><main className="auth-card">
        <span className="auth-kicker">GARAM MIND</span>
        <h1>네이버 로그인</h1>
        {message ? <>
            <p role="alert">{message}</p>
            <p className="auth-switch"><Link to="/login" replace>로그인으로 돌아가기</Link></p>
        </> : <p role="status">로그인을 완료하고 있습니다…</p>}
    </main></div>;
}

NaverLoginCallback.propTypes = { children: PropTypes.node.isRequired };
